import { forwardRef } from 'react';

import { type TaskType, TodoListWidget } from '@crehana/compass.dashlets';
import { formatDate } from '@crehana/date';
import { useTranslation } from '@crehana/i18n';

import { useGetTodoListWorkflows } from '../hooks/use-get-todo-list-workflows';
import { useHomeTranslations } from '../hooks/use-home-translations';

const TodoListAdminWidget = forwardRef<HTMLDivElement, unknown>(
  (_props, ref: React.ForwardedRef<HTMLDivElement>) => {
    const { t } = useHomeTranslations();
    const { languageCode } = useTranslation();
    const {
      loading: loadingWorkflows,
      workflowsData,
      error: errorWorkflows,
      refetch: retryWorkflows,
      currentPage,
      totalPages,
      totalCount,
      handlePageChange,
    } = useGetTodoListWorkflows();

    const transformWorkflowsData = () => {
      if (!workflowsData) {
        return {
          items: [],
          total: 0,
          tabs: [],
          totalPages: 1,
        };
      }

      const tabs = [
        {
          value: 'all',
          label: t('TODO_LIST_ADMIN_WIDGET_TAB_ALL'),
        },
        {
          value: 'management',
          label: t('TODO_LIST_ADMIN_WIDGET_TAB_MANAGEMENT'),
        },
      ];

      const items = workflowsData.edges.map((edge, index) => {
        const task = edge.node.task;

        let taskType: TaskType = 'WORKFLOW_CUSTOM_TASK' as TaskType;
        if (task.task_type === 'DOCUMENTS') {
          taskType = 'WORKFLOW_DOCUMENT_TO_SIGN' as TaskType;
        } else if (task.task_type === 'COMPLETE_PROFILE') {
          taskType = 'WORKFLOW_COMPLETE_PROFILE' as TaskType;
        }

        const daysRemaining = task.days_complete_task;

        return {
          id: `workflow-${index}`,
          type: taskType,
          taskName: task.name,
          documentName: '',
          endDate: formatDate(edge.node.limit_date, {
            inputFormat: 'YYYY-MM-DD',
            outputFormat: 'DD/MM/YYYY',
            languageCode: languageCode,
          }),
          daysRemaining: daysRemaining ?? 0,
        };
      });

      return {
        items,
        total: totalCount,
        tabs,
        totalPages,
      };
    };

    const workflowsListData = transformWorkflowsData();

    return (
      <div ref={ref} className="tw-w-full tw-h-full">
        <TodoListWidget
          total={workflowsListData.total}
          tabs={workflowsListData.tabs}
          items={workflowsListData.items}
          totalPages={workflowsListData.totalPages}
          currentPage={currentPage}
          loading={loadingWorkflows}
          error={Boolean(errorWorkflows)}
          refetch={retryWorkflows}
          onPageChange={handlePageChange}
        />
      </div>
    );
  },
);

TodoListAdminWidget.displayName = 'TodoListAdminWidget';

export { TodoListAdminWidget };
